// Maximum file size from environment variable or default to 100MB
const MAX_FILE_SIZE = process.env.REACT_APP_MAX_FILE_SIZE
  ? parseInt(process.env.REACT_APP_MAX_FILE_SIZE, 10)
  : 100 * 1024 * 1024; // 100MB in bytes

// Allowed file types from environment variable or default with expanded support
const ALLOWED_FILE_TYPES = process.env.REACT_APP_ALLOWED_FILE_TYPES
  ? process.env.REACT_APP_ALLOWED_FILE_TYPES.split(',')
  : [
      // Images
      'image/*',
      
      // Videos
      'video/*',
      
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/rtf',
      'text/plain',
      'application/epub+zip', // .epub
      
      // Data Formats
      'application/json',
      'application/xml',
      'text/csv',
      'text/html',
      'text/css',
      'text/javascript',
      'application/javascript',
      'application/x-yaml', // .yaml, .yml
      'text/markdown',
      'text/x-markdown',
      
      // Archive Formats
      'application/zip',
      'application/x-rar-compressed',
      'application/gzip',
      'application/x-7z-compressed',
      'application/x-tar',
      
      // Audio
      'audio/*',
      
      // 3D Models
      'model/gltf-binary', // .glb
      'model/gltf+json', // .gltf
      'model/obj', // .obj
      'model/stl', // .stl
      
      // Other Common Types
      'application/octet-stream', // binary data
      'text/calendar' // .ics
    ];

// Extensions allowed even when the browser reports no MIME type (or a generic
// one like application/octet-stream), since these formats aren't registered
// with a standard MIME type in most browsers.
const ALLOWED_EXTENSIONS_WITHOUT_RELIABLE_MIME = ['vox'];

/**
 * Validates a file based on size and type
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result with isValid and message
 */
export const validateFile = (file) => {
  // Check if file exists
  if (!file) {
    return {
      isValid: false,
      message: 'No file selected',
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      message: `File size exceeds the maximum limit of ${formatFileSize(MAX_FILE_SIZE)}`,
    };
  }

  // Check file type (falls back to extension for formats without a reliable MIME type, e.g. .vox)
  const extension = getFileExtension(file.name).replace('.', '').toLowerCase();
  const fileTypeValid =
    isValidFileType(file.type) || ALLOWED_EXTENSIONS_WITHOUT_RELIABLE_MIME.includes(extension);
  if (!fileTypeValid) {
    return {
      isValid: false,
      message: `File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}`,
    };
  }

  return {
    isValid: true,
    message: 'File is valid',
  };
};

/**
 * Checks if a file type is valid based on the allowed types
 * @param {string} fileType - The MIME type of the file
 * @returns {boolean} - Whether the file type is valid
 */
const isValidFileType = (fileType) => {
  return ALLOWED_FILE_TYPES.some((allowedType) => {
    // Handle wildcard like 'image/*'
    if (allowedType.endsWith('/*')) {
      const type = allowedType.split('/')[0];
      return fileType.startsWith(`${type}/`);
    }
    return fileType === allowedType;
  });
};

/**
 * Formats file size in bytes to a readable string
 * @param {number} bytes - The file size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} bytes`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Gets the file extension from a file name
 * @param {string} fileName - The name of the file
 * @returns {string} - The file extension
 */
export const getFileExtension = (fileName) => {
  return fileName.substring(fileName.lastIndexOf('.') || 0);
};

/**
 * Gets file metadata
 * @param {File} file - The file to get metadata from
 * @returns {Object} - File metadata
 */
export const getFileMetadata = (file) => {
  if (!file) return null;

  return {
    name: file.name,
    type: file.type || 'Unknown file type',
    size: formatFileSize(file.size),
    lastModified: new Date(file.lastModified).toLocaleString(),
  };
};

/**
 * Determines the file type category based on MIME type and extension
 * @param {string} mimeType - The MIME type of the file
 * @param {string} fileName - The name of the file
 * @returns {string} - Category of the file
 */
export const getFileTypeCategory = (mimeType, fileName = '') => {
  // Get file extension
  const extension = fileName.toLowerCase().split('.').pop();
  
  // Image types
  if (mimeType.startsWith('image/')) {
    return 'image';
  }
  
  // Video types
  if (mimeType.startsWith('video/')) {
    return 'video';
  }
  
  // Audio types
  if (mimeType.startsWith('audio/')) {
    return 'audio';
  }
  
  // PDF
  if (mimeType === 'application/pdf') {
    return 'pdf';
  }
  
  // Text files
  if (mimeType.startsWith('text/') || 
      ['md', 'markdown', 'txt', 'rtf'].includes(extension)) {
    return 'text';
  }
  
  // Code files
  if (['json', 'xml', 'html', 'css', 'js', 'jsx', 'ts', 'tsx', 'yml', 'yaml', 'csv'].includes(extension) ||
      mimeType === 'application/json' ||
      mimeType === 'application/xml' ||
      mimeType === 'text/html' ||
      mimeType === 'text/css' ||
      mimeType === 'text/javascript' ||
      mimeType === 'application/javascript') {
    return 'code';
  }
  
  // Document files
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'epub'].includes(extension) ||
      mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      mimeType === 'application/vnd.ms-powerpoint' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      mimeType === 'application/epub+zip') {
    return 'document';
  }
  
  // 3D Model files
  if (['gltf', 'glb', 'obj', 'stl', 'vox'].includes(extension) ||
      mimeType.startsWith('model/')) {
    return 'model';
  }
  
  // Archive files
  if (['zip', 'rar', 'gz', '7z', 'tar'].includes(extension) ||
      mimeType.includes('compressed') ||
      mimeType.includes('archive') ||
      mimeType === 'application/zip' ||
      mimeType === 'application/x-rar-compressed' ||
      mimeType === 'application/gzip' ||
      mimeType === 'application/x-7z-compressed' ||
      mimeType === 'application/x-tar') {
    return 'archive';
  }
  
  // Default to binary
  return 'binary';
};

const fileUtils = {
  validateFile,
  formatFileSize,
  getFileExtension,
  getFileMetadata,
  getFileTypeCategory,
};

export default fileUtils;